import { useCallback, useRef, useState, type SetStateAction } from 'react';

export type CanvasSize = { width: number; height: number };

type GuideSuppression = { x: number[]; y: number[] };

type Snapshot<T> = {
  fields: T[];
  imgSize: CanvasSize;
  suppressedGuides: GuideSuppression;
  /** Present only for actions that change the background, such as scale and clip. */
  svgMarkup?: string;
};

const HISTORY_LIMIT = 80;

const fieldsEqual = (a: unknown, b: unknown): boolean => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

function cloneFields<T>(fields: T[]): T[] {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(fields);
    } catch {
      /* fall through */
    }
  }
  return JSON.parse(JSON.stringify(fields)) as T[];
}

export function svgElementFromMarkup(markup: string): SVGSVGElement | null {
  const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;
  const el = doc.querySelector('svg');
  if (!el) return null;
  (el as SVGSVGElement).style.pointerEvents = 'none';
  return el as SVGSVGElement;
}

/**
 * Undo/redo for the form builder.
 * Drag and resize stay off the stack until the gesture ends, so one move is one Ctrl+Z.
 * Arrow-key nudges inside `coalesceMs` share a single step.
 */
export function useFormHistory<T>(params: {
  fields: T[];
  setFields: (value: SetStateAction<T[]>) => void;
  imgSize: CanvasSize;
  setImgSize: (value: SetStateAction<CanvasSize>) => void;
  getSvgMarkup: () => string | null;
  setSvgMarkup: (markup: string) => void;
  suppressedGuides: GuideSuppression;
  setSuppressedGuides: (value: GuideSuppression) => void;
}) {
  const { fields, setFields, imgSize, setImgSize, getSvgMarkup, setSvgMarkup, suppressedGuides, setSuppressedGuides } = params;

  const fieldsRef = useRef(fields);
  const imgRef = useRef(imgSize);
  const guidesRef = useRef(suppressedGuides);
  fieldsRef.current = fields;
  imgRef.current = imgSize;
  guidesRef.current = suppressedGuides;

  const pastRef = useRef<Snapshot<T>[]>([]);
  const futureRef = useRef<Snapshot<T>[]>([]);
  const gestureRef = useRef<Snapshot<T> | null>(null);
  const coalesceUntilRef = useRef(0);
  const applyingRef = useRef(false);
  const [, setRevision] = useState(0);
  const refresh = () => setRevision((n) => n + 1);

  const capture = useCallback((withSvg: boolean): Snapshot<T> => {
    const snap: Snapshot<T> = {
      fields: cloneFields(fieldsRef.current),
      imgSize: { ...imgRef.current },
      suppressedGuides: { x: guidesRef.current.x.slice(), y: guidesRef.current.y.slice() },
    };
    if (withSvg) {
      const markup = getSvgMarkup();
      if (markup) snap.svgMarkup = markup;
    }
    return snap;
  }, [getSvgMarkup]);

  const apply = useCallback((snap: Snapshot<T>) => {
    applyingRef.current = true;
    const nextFields = cloneFields(snap.fields);
    fieldsRef.current = nextFields;
    imgRef.current = { ...snap.imgSize };
    guidesRef.current = { x: snap.suppressedGuides.x.slice(), y: snap.suppressedGuides.y.slice() };
    setFields(nextFields);
    setImgSize({ ...snap.imgSize });
    setSuppressedGuides({ x: snap.suppressedGuides.x.slice(), y: snap.suppressedGuides.y.slice() });
    if (snap.svgMarkup) setSvgMarkup(snap.svgMarkup);
    applyingRef.current = false;
  }, [setFields, setImgSize, setSvgMarkup, setSuppressedGuides]);

  const push = useCallback((snap: Snapshot<T>) => {
    pastRef.current.push(snap);
    if (pastRef.current.length > HISTORY_LIMIT) pastRef.current.shift();
    futureRef.current = [];
    refresh();
  }, []);

  /** Live updates (drag/resize frames). Does not record history. */
  const setFieldsSilent = useCallback((action: SetStateAction<T[]>) => {
    setFields((prev) => {
      const next = typeof action === 'function' ? (action as (p: T[]) => T[])(prev) : action;
      fieldsRef.current = next;
      return next;
    });
  }, [setFields]);

  const updateFields = useCallback((action: SetStateAction<T[]>, opts?: { coalesceMs?: number }) => {
    if (!applyingRef.current && !gestureRef.current) {
      const now = Date.now();
      const windowMs = opts?.coalesceMs ?? 0;
      const inBurst = windowMs > 0 && now < coalesceUntilRef.current;
      if (!inBurst) push(capture(false));
      coalesceUntilRef.current = windowMs > 0 ? now + windowMs : 0;
    }
    setFieldsSilent(action);
  }, [capture, push, setFieldsSilent]);

  /** Record the current layout before a change that also touches the canvas (scale, clip). */
  const checkpoint = useCallback((opts?: { captureSvg?: boolean }) => {
    if (applyingRef.current) return;
    coalesceUntilRef.current = 0;
    push(capture(!!opts?.captureSvg));
  }, [capture, push]);

  const beginGesture = useCallback(() => {
    if (gestureRef.current || applyingRef.current) return;
    coalesceUntilRef.current = 0;
    gestureRef.current = capture(false);
  }, [capture]);

  const endGesture = useCallback(() => {
    const snap = gestureRef.current;
    if (!snap) return;
    gestureRef.current = null;
    const sizeSame = snap.imgSize.width === imgRef.current.width && snap.imgSize.height === imgRef.current.height;
    const guidesSame = fieldsEqual(snap.suppressedGuides, guidesRef.current);
    if (sizeSame && guidesSame && fieldsEqual(snap.fields, fieldsRef.current)) return;
    push(snap);
  }, [push]);

  const undo = useCallback(() => {
    if (gestureRef.current || pastRef.current.length === 0) return false;
    const prev = pastRef.current.pop()!;
    futureRef.current.push(capture(prev.svgMarkup != null));
    apply(prev);
    refresh();
    return true;
  }, [apply, capture]);

  const redo = useCallback(() => {
    if (gestureRef.current || futureRef.current.length === 0) return false;
    const next = futureRef.current.pop()!;
    pastRef.current.push(capture(next.svgMarkup != null));
    apply(next);
    refresh();
    return true;
  }, [apply, capture]);

  const clearHistory = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    gestureRef.current = null;
    coalesceUntilRef.current = 0;
    refresh();
  }, []);

  return {
    setFieldsSilent,
    updateFields,
    checkpoint,
    beginGesture,
    endGesture,
    undo,
    redo,
    clearHistory,
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
  };
}
